import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Users, Calendar, Clock, Search, ChevronLeft, AlertTriangle, Home, LogOut } from 'lucide-react';
import API_URL from '../../config';
import '../Home.css';
import './FacultyHome.css';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEPTS = ["CSE", "CSE-AIML&IOT", "AIDS", "CSBS", "IT", "ECE", "EEE", "EIE", "MECH", "CIVIL", "BIOTECH", "DS", "CYS", "R&AI"];
const YEARS = ["1st year", "2nd year", "3rd year", "4th year"];
const SECTIONS = ["A", "B", "C", "D"];

const ALL_SLOTS_1ST = [
  { start: '09:00', end: '10:00', label: '9:00 – 10:00' },
  { start: '10:00', end: '11:00', label: '10:00 – 11:00' },
  { start: '11:00', end: '12:00', label: '11:00 – 12:00' },
  { start: '12:00', end: '12:40', label: '12:00 – 12:40' },
  { start: '12:40', end: '13:00', label: '12:40 – 1:00' },
  { start: '13:00', end: '13:40', label: '1:00 – 1:40' },
  { start: '13:40', end: '14:40', label: '1:40 – 2:40' },
  { start: '14:40', end: '15:40', label: '2:40 – 3:40' },
  { start: '15:40', end: '16:40', label: '3:40 – 4:40' },
];

const ALL_SLOTS_SENIOR = [
  { start: '09:00', end: '10:00', label: '9:00 – 10:00' },
  { start: '10:00', end: '11:00', label: '10:00 – 11:00' },
  { start: '11:00', end: '12:00', label: '11:00 – 12:00' },
  { start: '12:00', end: '13:00', label: '12:00 – 1:00' },
  { start: '13:00', end: '13:40', label: '1:00 – 1:40' },
  { start: '13:40', end: '14:40', label: '1:40 – 2:40' },
  { start: '14:40', end: '15:40', label: '2:40 – 3:40' },
  { start: '15:40', end: '16:40', label: '3:40 – 4:40' },
];

const AttendanceAdmin = ({ inlineMode = false }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('timetable');

  // Timetable state
  const [selYear, setSelYear] = useState('2nd year');
  const [selBranch, setSelBranch] = useState('CSE');
  const [selSection, setSelSection] = useState('A');
  const [classGrid, setClassGrid] = useState([]);
  const [classInfo, setClassInfo] = useState({ coordinator: null, hod: null });
  const [loadingGrid, setLoadingGrid] = useState(false);

  // Override modal
  const [overrideCell, setOverrideCell] = useState(null);
  const [facultySearch, setFacultySearch] = useState('');
  const [allFaculty, setAllFaculty] = useState([]);
  const [filteredFaculty, setFilteredFaculty] = useState([]);
  const [overrideForm, setOverrideForm] = useState({ faculty_email: '', faculty_name: '', subject: '', block_type: 'class', room_no: '', is_temporary: false });
  const [overrideSaving, setOverrideSaving] = useState(false);

  // Classroom state
  const [classroom, setClassroom] = useState('');
  const [isEditingRoom, setIsEditingRoom] = useState(false);
  const [tempRoom, setTempRoom] = useState('');

  // Role requests state
  const [roleRequests, setRoleRequests] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Active roles state (source of truth from faculty table)
  const [activeRoles, setActiveRoles] = useState({ coordinators: [], hods: [] });
  const [loadingActiveRoles, setLoadingActiveRoles] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
    if (!userData.email || (userData.role !== 'faculty' && userData.role !== 'admin')) {
      navigate('/');
      return;
    }
    setUser(userData);
    fetchAllFaculty();
  }, [navigate]);

  const fetchAllFaculty = async () => {
    try {
      const res = await fetch(`${API_URL}/timetable/faculty-list`);
      const data = await res.json();
      setAllFaculty(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  const fetchClassGrid = useCallback(async () => {
    setLoadingGrid(true);
    try {
      const res = await fetch(`${API_URL}/timetable/class/${encodeURIComponent(selYear)}/${encodeURIComponent(selBranch)}/${encodeURIComponent(selSection)}`);
      const data = await res.json();
      setClassGrid(Array.isArray(data) ? data : []);

      // Fetch classroom
      const roomRes = await fetch(`${API_URL}/timetable/classroom/${encodeURIComponent(selYear)}/${encodeURIComponent(selBranch)}/${encodeURIComponent(selSection)}`);
      const roomData = await roomRes.json();
      setClassroom(roomData.room_no || '');
      setTempRoom(roomData.room_no || '');

      // Fetch coordinator and HOD info for this class
      const facRes = await fetch(`${API_URL}/faculty/`);
      const facData = await facRes.json();
      const fac = facData.faculty || [];
      const coord = fac.find(f => f.is_coordinator && f.coordinator_branch === selBranch && f.coordinator_year === selYear && f.coordinator_section === selSection);
      const hod = fac.find(f => f.is_hod && f.hod_branch === selBranch);
      setClassInfo({ coordinator: coord || null, hod: hod || null });
    } catch (e) { console.error(e); }
    finally { setLoadingGrid(false); }
  }, [selYear, selBranch, selSection]);

  const saveClassroom = async () => {
    try {
      const res = await fetch(`${API_URL}/timetable/classroom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year: selYear,
          branch: selBranch,
          section: selSection,
          room_no: tempRoom,
          admin_email: user.email
        })
      });
      if (res.ok) {
        setClassroom(tempRoom);
        setIsEditingRoom(false);
      } else {
        alert('Failed to save classroom');
      }
    } catch (e) {
      console.error(e);
      alert('Error saving classroom');
    }
  };

  const handleClearTimetable = async () => {
    const confirmClear = window.confirm(
      `⚠️ WARNING: Are you sure you want to clear the ENTIRE timetable for ${selYear} ${selBranch} Section ${selSection}?\n\nThis will permanently delete all entries for this class. This action cannot be undone.`
    );
    if (!confirmClear) return;

    try {
      const res = await fetch(
        `${API_URL}/timetable/class/${encodeURIComponent(selYear)}/${encodeURIComponent(selBranch)}/${encodeURIComponent(selSection)}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'x-user-email': user?.email || '',
          },
        }
      );
      const data = await res.json();
      if (res.ok) {
        alert(data.message || 'Timetable cleared successfully.');
        fetchClassGrid();
      } else {
        alert(data.error || 'Failed to clear timetable.');
      }
    } catch (e) {
      console.error(e);
      alert('Error clearing timetable.');
    }
  };

  useEffect(() => {
    if (activeTab === 'timetable') fetchClassGrid();
  }, [activeTab, selYear, selBranch, selSection, fetchClassGrid]);

  const fetchRoleRequests = useCallback(async () => {
    setLoadingRoles(true);
    try {
      const res = await fetch(`${API_URL}/faculty/role-requests`);
      const data = await res.json();
      setRoleRequests(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoadingRoles(false); }
  }, []);

  const fetchActiveRoles = useCallback(async () => {
    setLoadingActiveRoles(true);
    try {
      const res = await fetch(`${API_URL}/faculty/active-roles`);
      const data = await res.json();
      setActiveRoles(data || { coordinators: [], hods: [] });
    } catch (e) { console.error(e); }
    finally { setLoadingActiveRoles(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'roles') fetchRoleRequests();
    if (activeTab === 'present-roles') fetchActiveRoles();
  }, [activeTab, fetchRoleRequests, fetchActiveRoles]);

  // Faculty typeahead
  useEffect(() => {
    if (!facultySearch.trim()) { setFilteredFaculty([]); return; }
    const term = facultySearch.toLowerCase();
    setFilteredFaculty(allFaculty.filter(f => (f.name && f.name.toLowerCase().includes(term)) || (f.email && f.email.toLowerCase().includes(term))).slice(0, 6));
  }, [facultySearch, allFaculty]);

  const getBlocksForDaySlot = (day, slot) => {
    return classGrid.filter(b => b.day_of_week === day && b.start_time === slot.start);
  };

  const openOverride = (day, slot) => {
    const existing = classGrid.find(b => b.day_of_week === day && b.start_time === slot.start);
    setOverrideCell({ day, slot });
    setFacultySearch(existing?.is_temporary ? 'Temporary Placeholder' : (existing?.faculty_name || existing?.faculty_email || ''));
    setOverrideForm({
      faculty_email: existing?.faculty_email || '',
      faculty_name: existing?.faculty_name || '',
      subject: existing?.subject || '',
      block_type: existing?.block_type || 'class',
      room_no: existing?.room_no || '',
      is_temporary: !!existing?.is_temporary
    });
  };

  const saveOverride = async () => {
    if (!overrideForm.is_temporary && !overrideForm.faculty_email) {
      return alert('Please select a faculty member or mark this slot as temporary');
    }
    setOverrideSaving(true);
    try {
      const isTemp = !!overrideForm.is_temporary;
      const fEmail = isTemp ? (overrideForm.faculty_email || 'placeholder@vjlink.in') : overrideForm.faculty_email;
      const fName = isTemp ? (overrideForm.faculty_name || 'Placeholder') : overrideForm.faculty_name;

      const payload = {
        admin_email: user.email,
        faculty_email: fEmail,
        faculty_name: fName,
        day_of_week: overrideCell.day,
        start_time: overrideCell.slot.start,
        end_time: overrideCell.slot.end,
        period_number: overrideCell.slot.period || 1,
        year: selYear,
        branch: selBranch,
        section: selSection,
        subject: overrideForm.subject,
        block_type: overrideForm.block_type || 'class',
        room_no: overrideForm.block_type === 'lab' ? overrideForm.room_no : null,
        is_temporary: isTemp
      };
      const res = await fetch(`${API_URL}/timetable/admin-override`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) { setOverrideCell(null); fetchClassGrid(); }
      else alert('Override failed');
    } catch (e) { alert('Error saving override'); }
    finally { setOverrideSaving(false); }
  };

  const handleRoleAction = async (requestId, action) => {
    try {
      const res = await fetch(`${API_URL}/faculty/approve-role`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: requestId, action })
      });
      if (res.ok) { fetchRoleRequests(); }
      else alert('Action failed');
    } catch (e) { alert('Error processing request'); }
  };

  if (!user) return null;

  const slots = selYear === '1st year' ? ALL_SLOTS_1ST : ALL_SLOTS_SENIOR;

  const inner = (
    <div style={{ maxWidth: '1300px', margin: '0 auto' }}>
      {!inlineMode && (
        <header className="home-header" style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/faculty/home')} className="back-btn-modern" style={{ position: 'static', transform: 'none', flexShrink: 0 }}>
            <ChevronLeft size={20} /> Back
          </button>
          <div style={{ flex: 1, minWidth: '120px' }}>
            <h1 className="brand-title" style={{ fontSize: '2rem', margin: 0 }}>Attendance Admin</h1>
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>View class timetables & approve faculty role requests.</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button onClick={() => navigate('/faculty/home')} title="Home" style={{ width: 46, height: 46, borderRadius: '14px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0 }}><Home size={20} /></button>
            <button onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }} title="Logout" style={{ width: 46, height: 46, borderRadius: '14px', border: 'none', background: '#ef4444', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(239,68,68,0.4)', flexShrink: 0 }}><LogOut size={20} /></button>
          </div>
        </header>
      )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          {[
            { id: 'timetable', label: '📅 Class Timetable View' },
            { id: 'present-roles', label: `✅ Present Roles${(activeRoles.coordinators.length + activeRoles.hods.length) > 0 ? ` (${activeRoles.coordinators.length + activeRoles.hods.length})` : ''}` },
            { id: 'roles', label: `📥 Role Requests${roleRequests.length > 0 ? ` (${roleRequests.length})` : ''}` }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: '0.8rem 2rem', borderRadius: '30px', border: '1px solid var(--glass-border)', background: activeTab === tab.id ? 'var(--accent-color)' : 'var(--glass-bg)', color: activeTab === tab.id ? 'white' : 'var(--text-main)', cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.3s' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ==== TIMETABLE VIEW ==== */}
        {activeTab === 'timetable' && (
          <>
            {/* Class selector */}
            <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {[
                { label: 'Year', value: selYear, setter: setSelYear, options: YEARS },
                { label: 'Branch', value: selBranch, setter: val => {
                  setSelBranch(val);
                  if (val === 'CSE-AIML&IOT' && selSection === 'D') {
                    setSelSection('IOT');
                  }
                }, options: DEPTS },
                { label: 'Section', value: selSection, setter: setSelSection, options: selBranch === 'CSE-AIML&IOT' ? ["A", "B", "C", "IOT"] : SECTIONS },
              ].map(f => (
                <div key={f.label} style={{ flex: 1, minWidth: '150px' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>{f.label}</label>
                  <select value={f.value} onChange={e => f.setter(e.target.value)} className="modern-select" style={{ width: '100%', padding: '0.8rem', marginTop: '0.5rem' }}>
                    {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              ))}
              <button onClick={fetchClassGrid}
                style={{ padding: '0.8rem 1.5rem', borderRadius: '8px', background: 'var(--glass-bg)', color: 'var(--text-main)', border: '1px solid var(--glass-border)', cursor: 'pointer', fontWeight: 'bold' }}>
                Refresh
              </button>
              {(user?.role === 'admin' || ["24071a6691@vnrvjiet.in", "24071a6679@vnrvjiet.in"].includes(user?.email)) && (
                <button onClick={handleClearTimetable}
                  style={{ padding: '0.8rem 1.5rem', borderRadius: '8px', background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 2px 8px rgba(239,68,68,0.4)' }}>
                  Clear Timetable
                </button>
              )}
            </div>

            {/* Class Info Banner (HOD + Coordinator) */}
            <div className="glass-card" style={{ padding: '1rem 1.5rem', marginBottom: '1.5rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>HOD</span>
                <div style={{ fontWeight: 'bold', color: classInfo.hod ? 'var(--accent-color)' : 'var(--text-muted)' }}>
                  {classInfo.hod ? `${classInfo.hod.name} (${classInfo.hod.email})` : 'Not assigned'}
                </div>
              </div>
              <div>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Class Coordinator</span>
                <div style={{ fontWeight: 'bold', color: classInfo.coordinator ? 'var(--accent-color)' : 'var(--text-muted)' }}>
                  {classInfo.coordinator ? `${classInfo.coordinator.name} (${classInfo.coordinator.email})` : 'Not assigned'}
                </div>
              </div>
              <div style={{ minWidth: '180px' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Classroom Location</span>
                {isEditingRoom ? (
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.2rem' }}>
                    <input value={tempRoom} onChange={e => setTempRoom(e.target.value)} placeholder="e.g. B-302"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', color: 'white', padding: '0.3rem 0.5rem', borderRadius: '6px', width: '90px', fontSize: '0.85rem' }} />
                    <button onClick={saveClassroom} style={{ background: 'var(--accent-color)', color: 'white', border: 'none', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}>Save</button>
                    <button onClick={() => { setIsEditingRoom(false); setTempRoom(classroom); }} style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '0.3rem 0.6rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.2rem' }}>
                    <span style={{ color: classroom ? 'var(--text-main)' : 'var(--text-muted)' }}>{classroom || 'Not set'}</span>
                    {(user?.role === 'admin' || ["24071a6691@vnrvjiet.in", "24071a6679@vnrvjiet.in"].includes(user?.email)) && (
                      <button onClick={() => setIsEditingRoom(true)} style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>✏️ Edit</button>
                    )}
                  </div>
                )}
              </div>
              <div style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
                Click any cell to override · Changes reflect on faculty's schedule
              </div>
            </div>

            {/* Grid */}
            {loadingGrid ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading class timetable...</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: '1000px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid var(--glass-border)', color: 'var(--text-muted)', minWidth: '130px' }}>
                        <Clock size={14} style={{ marginRight: 4 }} /> Time
                      </th>
                      {DAYS.map(day => (
                        <th key={day} style={{ padding: '1rem', textAlign: 'center', borderBottom: '1px solid var(--glass-border)', color: 'var(--accent-color)', fontWeight: 'bold' }}>
                          {day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map(slot => (
                      <tr key={slot.start}>
                        <td style={{ padding: '0.5rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                          {slot.label}
                        </td>
                        {DAYS.map(day => {
                          const blocks = getBlocksForDaySlot(day, slot);
                          const hasBlocks = blocks.length > 0;
                          const hasLab = blocks.some(b => b.block_type === 'lab');
                          return (
                            <td key={day} style={{ padding: '0.4rem', borderBottom: '1px solid var(--glass-border)', verticalAlign: 'top' }}>
                              <div
                                onClick={() => openOverride(day, slot)}
                                style={{
                                  minHeight: '65px',
                                  borderRadius: '8px',
                                  padding: '0.6rem',
                                  cursor: 'pointer',
                                  background: hasBlocks ? (hasLab ? 'rgba(139,92,246,0.1)' : 'rgba(99,102,241,0.12)') : 'var(--glass-bg)',
                                  border: hasBlocks ? (hasLab ? '1px solid rgba(139,92,246,0.4)' : '1px solid rgba(99,102,241,0.3)') : '1px dashed var(--glass-border)',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '0.3rem'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = hasBlocks ? (hasLab ? 'rgba(139,92,246,0.2)' : 'rgba(99,102,241,0.2)') : 'var(--glass-hover)'}
                                onMouseLeave={e => e.currentTarget.style.background = hasBlocks ? (hasLab ? 'rgba(139,92,246,0.1)' : 'rgba(99,102,241,0.12)') : 'var(--glass-bg)'}
                              >
                                {hasBlocks ? (
                                  blocks.map((block, idx) => (
                                    <div key={block.id || idx} style={{
                                      fontSize: '0.72rem',
                                      borderRadius: '5px',
                                      padding: '0.25rem 0.4rem',
                                      background: block.block_type === 'lab' ? 'rgba(139,92,246,0.15)' : 'rgba(99,102,241,0.1)',
                                      borderLeft: `2px solid ${block.block_type === 'lab' ? '#8b5cf6' : 'var(--accent-color)'}`
                                    }}>
                                      <div style={{ fontWeight: 'bold', color: block.block_type === 'lab' ? '#a78bfa' : 'var(--accent-color)' }}>
                                        {block.block_type === 'lab' ? '🧪' : '📚'} {block.faculty_name || block.faculty_email}
                                      </div>
                                      <div style={{ color: 'var(--text-secondary)', marginTop: '0.1rem' }}>{block.subject || '—'}</div>
                                    </div>
                                  ))
                                ) : (
                                  <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center', paddingTop: '0.5rem' }}>Free Period</div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ==== ROLE REQUESTS (Inbox) ==== */}
        {activeTab === 'roles' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ padding: '0.8rem 1rem', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '10px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              📥 Pending role requests from faculty. Approving sets them as the active Coordinator/HOD and removes any previous holder.
            </div>
            {loadingRoles ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading role requests...</div>
            ) : roleRequests.length === 0 ? (
              <div className="glass-card" style={{ padding: '3rem', textAlign: 'center' }}>
                <CheckCircle size={40} style={{ opacity: 0.3, marginBottom: '1rem', color: '#10b981' }} />
                <p style={{ color: 'var(--text-secondary)' }}>No pending role requests. All clear!</p>
              </div>
            ) : roleRequests.map(req => (
              <div key={req.id} className="glass-card" style={{ padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{req.faculty_name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{req.faculty_email}</div>
                  <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
                    <span style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--accent-color)', padding: '0.3rem 0.8rem', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      {req.role_type === 'coordinator' ? '📌 Class Coordinator' : '🏫 HOD'}
                    </span>
                    {req.role_type === 'coordinator' && (
                      <span style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-secondary)', padding: '0.3rem 0.8rem', borderRadius: '20px', fontSize: '0.85rem' }}>
                        {req.year} – {req.branch} – Section {req.section}
                      </span>
                    )}
                    {req.role_type === 'hod' && (
                      <span style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-secondary)', padding: '0.3rem 0.8rem', borderRadius: '20px', fontSize: '0.85rem' }}>
                        Dept: {req.branch}
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    Requested: {new Date(req.created_at).toLocaleString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <button onClick={() => handleRoleAction(req.id, 'approve')}
                    style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.8rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <CheckCircle size={16} /> Approve
                  </button>
                  <button onClick={() => handleRoleAction(req.id, 'reject')}
                    style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: 'none', padding: '0.8rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <XCircle size={16} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ==== PRESENT ROLES (Active from faculty table) ==== */}
        {activeTab === 'present-roles' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ padding: '0.8rem 1rem', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '10px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              ✅ These are the currently active roles stored in the faculty table. All attendance routing uses this data. You can remove a role here — the faculty can then submit a new request.
            </div>

            {loadingActiveRoles ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>Loading active roles...</div>
            ) : (
              <>
                {/* Coordinators */}
                <div className="glass-card" style={{ padding: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: 'var(--accent-color)' }}>📌 Class Coordinators</h3>
                  {activeRoles.coordinators.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', padding: '1rem 0' }}>No coordinators assigned yet.</div>
                  ) : activeRoles.coordinators.map(c => (
                    <div key={c.email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem 0', borderBottom: '1px solid rgba(255,255,255,0.05)', gap: '1rem', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{c.name || c.email}</div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{c.email}</div>
                        <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                          {c.coordinator_year} · {c.coordinator_branch} · Section {c.coordinator_section}
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Remove ${c.name || c.email} as Coordinator?`)) return;
                          await fetch(`${API_URL}/faculty/remove-role/${encodeURIComponent(c.email)}`, {
                            method: 'DELETE', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ role_type: 'coordinator' })
                          });
                          fetchActiveRoles();
                        }}
                        style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        🗑 Remove Role
                      </button>
                    </div>
                  ))}
                </div>

                {/* HODs */}
                <div className="glass-card" style={{ padding: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#f59e0b' }}>🏫 HODs</h3>
                  {activeRoles.hods.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', padding: '1rem 0' }}>No HODs assigned yet.</div>
                  ) : activeRoles.hods.map(h => (
                    <div key={h.email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem 0', borderBottom: '1px solid rgba(255,255,255,0.05)', gap: '1rem', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{h.name || h.email}</div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{h.email}</div>
                        <div style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                          HOD — {h.hod_branch} Department
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Remove ${h.name || h.email} as HOD?`)) return;
                          await fetch(`${API_URL}/faculty/remove-role/${encodeURIComponent(h.email)}`, {
                            method: 'DELETE', headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ role_type: 'hod' })
                          });
                          fetchActiveRoles();
                        }}
                        style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                        🗑 Remove Role
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

      {/* Override Modal */}
      {overrideCell && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => { setOverrideCell(null); setFilteredFaculty([]); }}>
          <div className="glass-card" onClick={e => e.stopPropagation()} style={{ padding: '2rem', maxWidth: '450px', width: '90%', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 0.3rem 0' }}>Override Block</h3>
            <p style={{ color: 'var(--text-muted)', margin: '0 0 1.5rem 0', fontSize: '0.9rem' }}>
              {overrideCell.day} · {overrideCell.slot.label}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Block Type</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.4rem' }}>
                  {['class', 'lab'].map(t => (
                    <button key={t} type="button" onClick={() => setOverrideForm(f => ({ ...f, block_type: t }))}
                      style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid var(--glass-border)', background: overrideForm.block_type === t ? 'var(--accent-color)' : 'var(--glass-bg)', color: overrideForm.block_type === t ? 'white' : 'var(--text-main)', cursor: 'pointer', fontWeight: 'bold' }}>
                      {t === 'class' ? '📚 Class' : '🧪 Lab'}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Search Faculty</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={overrideForm.is_temporary} onChange={e => {
                      const checked = e.target.checked;
                      setOverrideForm(f => ({ ...f, is_temporary: checked }));
                      if (checked) {
                        setFacultySearch('Temporary Placeholder');
                        setOverrideForm(f => ({ ...f, is_temporary: true, faculty_email: 'placeholder@vjlink.in', faculty_name: 'Placeholder' }));
                      } else {
                        setFacultySearch('');
                        setOverrideForm(f => ({ ...f, is_temporary: false, faculty_email: '', faculty_name: '' }));
                      }
                    }} />
                    <span>Temporary / Placeholder</span>
                  </label>
                </div>
                {!overrideForm.is_temporary && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '0.5rem 0.8rem', border: '1px solid var(--glass-border)' }}>
                    <Search size={16} color="var(--text-muted)" />
                    <input value={facultySearch} onChange={e => { setFacultySearch(e.target.value); setOverrideForm(f => ({ ...f, faculty_email: '', faculty_name: '' })); }}
                      placeholder="Type faculty name or email..."
                      style={{ background: 'none', border: 'none', color: 'white', outline: 'none', flex: 1, fontSize: '0.9rem' }} />
                    {overrideForm.faculty_email && <CheckCircle size={16} color="#10b981" />}
                  </div>
                )}
                {filteredFaculty.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--glass-bg, #1e1e2e)', border: '1px solid var(--glass-border)', borderRadius: '8px', zIndex: 10, maxHeight: '200px', overflowY: 'auto' }}>
                    {filteredFaculty.map(f => (
                      <div key={f.email} onClick={() => { setOverrideForm(prev => ({ ...prev, faculty_email: f.email, faculty_name: f.name })); setFacultySearch(`${f.name} (${f.email})`); setFilteredFaculty([]); }}
                        style={{ padding: '0.7rem 1rem', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{f.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{f.email}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Subject</label>
                <input value={overrideForm.subject} onChange={e => setOverrideForm(f => ({ ...f, subject: e.target.value }))}
                  placeholder="e.g. Data Structures" className="search-input-modern"
                  style={{ width: '100%', padding: '0.7rem', marginTop: '0.4rem' }} />
              </div>
              {overrideForm.block_type === 'lab' && (
                <div>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 'bold' }}>Lab / Room Number</label>
                  <input value={overrideForm.room_no} onChange={e => setOverrideForm(f => ({ ...f, room_no: e.target.value }))}
                    placeholder="e.g. Lab 2, CSE Lab 3" className="search-input-modern"
                    style={{ width: '100%', padding: '0.7rem', marginTop: '0.4rem' }} />
                </div>
              )}
              {!overrideForm.is_temporary && !overrideForm.faculty_email && <div style={{ fontSize: '0.8rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <AlertTriangle size={14} /> Select a faculty from the dropdown
              </div>}
               <div style={{ display: 'flex', gap: '0.8rem' }}>
                <button onClick={() => { setOverrideCell(null); setFilteredFaculty([]); }}
                  style={{ flex: 1, padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'var(--glass-bg)', color: 'var(--text-main)', cursor: 'pointer', fontWeight: '600' }}>
                  Cancel
                </button>
                <button onClick={saveOverride} disabled={overrideSaving || (!overrideForm.is_temporary && !overrideForm.faculty_email)}
                  style={{ flex: 2, padding: '0.8rem', borderRadius: '8px', border: 'none', background: (overrideForm.is_temporary || overrideForm.faculty_email) ? 'var(--accent-color)' : 'var(--glass-bg)', color: (overrideForm.is_temporary || overrideForm.faculty_email) ? 'white' : 'var(--text-muted)', border: (overrideForm.is_temporary || overrideForm.faculty_email) ? 'none' : '1px solid var(--glass-border)', cursor: (overrideForm.is_temporary || overrideForm.faculty_email) ? 'pointer' : 'not-allowed', fontWeight: 'bold' }}>
                  {overrideSaving ? 'Saving...' : '✅ Save Override'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return inlineMode ? inner : <div className="home-page-container"><div className="home-content-wrapper">{inner}</div></div>;
};

export default AttendanceAdmin;
